<script setup lang="ts">
    import { computed, ref, watch, watchEffect } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import SelectInput from '@/js/components/SelectInput.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import EchoButton from '@/js/components/EchoButton.vue';
    import EchoTooltip from '@/js/components/EchoTooltip.vue';
    import LoadingDots from '@/js/components/LoadingDots.vue';
    import { InfoIcon } from '@lucide/vue';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import {
        useQueryGetCompanies,
        useQueryGetKeyIndicateUsers,
        useQueryGetAssignableTenantRoles,
        useMutationImportUsers,
    } from '@/js/services/api/backOffice/backOfficeQueries';

    const successDialogStore = useSuccessDialog();

    const { data: companiesData } = useQueryGetCompanies();
    const companies = computed(() =>
        (companiesData.value ?? []).filter((c) => c.tenantId !== null)
    );
    const selectedCompanyId = ref<number | ''>('');

    const selectedCompany = computed(
        () => companies.value.find((c) => c.companyId === selectedCompanyId.value) ?? null
    );
    const tenantId = computed(() => selectedCompany.value?.tenantId ?? '');
    const tenantName = computed(() => selectedCompany.value?.name ?? '');

    const showUserTable = ref(false);

    const { data: usersData, isLoading: usersLoading } = useQueryGetKeyIndicateUsers(
        tenantId,
        showUserTable
    );
    const { data: rolesData } = useQueryGetAssignableTenantRoles(tenantName);

    const users = computed(() => usersData.value ?? []);
    const roles = computed(() => rolesData.value ?? []);

    type RowState = { selected: boolean; selectedRoleId: string };
    const rowStates = ref<Record<string, RowState>>({});

    watch(selectedCompanyId, () => {
        showUserTable.value = false;
        rowStates.value = {};
    });

    const companySearch = ref<string | null>(null);

    const companyOptions = computed<Option[]>(() =>
        companies.value.map((c) => ({ name: c.name, value: String(c.companyId) }))
    );

    const onCompanySelect = (option: Option) => {
        selectedCompanyId.value = Number(option.value);
    };

    const onCompanyClear = () => {
        selectedCompanyId.value = '';
    };

    watch(
        users,
        (newUsers) => {
            const states: Record<string, RowState> = {};
            for (const user of newUsers) {
                const key = user.keyIndicateUser.email;
                states[key] = {
                    selected: true,
                    selectedRoleId: user.echoRole?.id ?? '',
                };
            }
            rowStates.value = states;
        },
        { immediate: true }
    );

    const allSelected = computed(
        () =>
            users.value.length > 0 &&
            users.value.every((u) => rowStates.value[u.keyIndicateUser.email]?.selected)
    );

    const someSelected = computed(() =>
        users.value.some((u) => rowStates.value[u.keyIndicateUser.email]?.selected)
    );

    const toggleAllSelected = () => {
        const newVal = !allSelected.value;
        for (const user of users.value) {
            const key = user.keyIndicateUser.email;
            if (rowStates.value[key]) rowStates.value[key].selected = newVal;
        }
    };

    const selectAllCheckbox = ref<HTMLInputElement | null>(null);
    watchEffect(() => {
        if (selectAllCheckbox.value) {
            selectAllCheckbox.value.indeterminate = someSelected.value && !allSelected.value;
        }
    });

    const selectedCount = computed(
        () => users.value.filter((u) => rowStates.value[u.keyIndicateUser.email]?.selected).length
    );

    const { mutateAsync: importUsersMutation, isPending: importIsPending } =
        useMutationImportUsers();

    type KeyIndicateUser = (typeof users.value)[0];

    const buildUserPayload = (user: KeyIndicateUser) => ({
        email: user.keyIndicateUser.email,
        firstName: user.keyIndicateUser.firstName,
        lastName: user.keyIndicateUser.lastName,
        roleId: rowStates.value[user.keyIndicateUser.email]?.selectedRoleId ?? '',
        employeeId: user.keyIndicateUser.echoEmployeeId,
    });

    const importSelected = async () => {
        if (!someSelected.value) return;
        const selected = users.value.filter(
            (u) => rowStates.value[u.keyIndicateUser.email]?.selected
        );
        await importUsersMutation({
            tenantId: tenantId.value,
            users: selected.map(buildUserPayload),
        });
        successDialogStore.openDialog({ message: __('back-office-import-agents-success') });
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-import-agents-from-v1') }}</h1>
        </template>
        <template v-if="showUserTable" #topbar>
            <span class="text-body-bold self-center">
                {{
                    __('back-office-import-agents-selected-count', {
                        selected: String(selectedCount),
                        total: String(users.length),
                    })
                }}
            </span>
            <div class="ml-4">
                <EchoButton
                    variant="primary"
                    :disabled="!someSelected"
                    :loading="importIsPending"
                    @click="importSelected"
                >
                    {{ __('back-office-import-selected') }}
                </EchoButton>
            </div>
        </template>
        <template #content>
            <form class="w-lg max-w-full" @submit.prevent>
                <FormGroup>
                    <AutoComplete
                        v-model="companySearch"
                        input-id="import-agents-company"
                        :label="__('back-office-select-customer')"
                        :options="companyOptions"
                        @select="onCompanySelect"
                        @clear="onCompanyClear"
                    />
                </FormGroup>
            </form>

            <div class="mt-8">
                <EchoButton
                    variant="primary"
                    :disabled="!selectedCompanyId"
                    :loading="usersLoading"
                    @click="showUserTable = true"
                >
                    {{ __('back-office-fetch-agents') }}
                </EchoButton>
            </div>

            <template v-if="showUserTable">
                <div class="mt-10">
                    <p class="mb-4 flex gap-2 max-w-xl text-body text-gray-medium">
                        <InfoIcon class="size-4 shrink-0 mt-0.5" />
                        {{ __('back-office-import-agents-duplicate-email-info') }}
                    </p>
                    <div class="mt-6 overflow-x-auto">
                        <table class="w-full border-collapse">
                            <thead>
                                <tr class="border-b border-gray-lightest">
                                    <th class="py-3 pr-4 w-10 text-left align-middle">
                                        <EchoTooltip
                                            :label="__('back-office-import-agents-select-all')"
                                        >
                                            <input
                                                ref="selectAllCheckbox"
                                                type="checkbox"
                                                class="Custom-checkbox cursor-pointer ml-2.5"
                                                :checked="allSelected"
                                                @change="toggleAllSelected"
                                            />
                                        </EchoTooltip>
                                    </th>
                                    <th
                                        class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase align-middle"
                                    >
                                        {{ __('users-table-column-name') }}
                                    </th>
                                    <th
                                        class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase align-middle"
                                    >
                                        {{ __('email') }}
                                    </th>
                                    <th
                                        class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase align-middle"
                                    >
                                        {{ __('back-office-import-agents-role-v1') }}
                                    </th>
                                    <th
                                        class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase align-middle"
                                    >
                                        {{ __('back-office-import-agents-role-v2') }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="relative">
                                <tr v-if="usersLoading">
                                    <td colspan="5" class="py-12 text-center">
                                        <LoadingDots />
                                    </td>
                                </tr>
                                <tr v-else-if="!users.length">
                                    <td
                                        colspan="5"
                                        class="py-6 text-body text-gray-medium text-center"
                                    >
                                        {{ __('back-office-import-agents-no-users') }}
                                    </td>
                                </tr>
                                <tr
                                    v-for="user in users"
                                    :key="user.keyIndicateUser.email"
                                    class="border-b border-gray-lightest cursor-pointer hover:bg-gray-50"
                                    @click="
                                        rowStates[user.keyIndicateUser.email].selected =
                                            !rowStates[user.keyIndicateUser.email].selected
                                    "
                                >
                                    <td class="py-3 pr-4 w-10" @click.stop>
                                        <input
                                            type="checkbox"
                                            class="Custom-checkbox cursor-pointer"
                                            :checked="
                                                rowStates[user.keyIndicateUser.email]?.selected
                                            "
                                            @change="
                                                rowStates[user.keyIndicateUser.email].selected =
                                                    !rowStates[user.keyIndicateUser.email].selected
                                            "
                                        />
                                    </td>
                                    <td class="py-3 pl-3 pr-6 text-body">
                                        {{
                                            [
                                                user.keyIndicateUser.firstName,
                                                user.keyIndicateUser.lastName,
                                            ]
                                                .filter(Boolean)
                                                .join(' ')
                                        }}
                                    </td>
                                    <td class="py-3 pl-3 pr-6 text-body">
                                        {{ user.keyIndicateUser.email }}
                                    </td>
                                    <td class="py-3 pl-3 pr-6 text-body">
                                        {{ user.keyIndicateUser.keyIndicateRoleName }}
                                    </td>
                                    <td class="py-3 pl-3 pr-6" @click.stop>
                                        <SelectInput
                                            v-if="rowStates[user.keyIndicateUser.email]"
                                            v-model="
                                                rowStates[user.keyIndicateUser.email].selectedRoleId
                                            "
                                            size="large"
                                        >
                                            <option value="" disabled></option>
                                            <option
                                                v-for="role in roles"
                                                :key="role.id"
                                                :value="role.id"
                                            >
                                                {{ role.name }}
                                            </option>
                                        </SelectInput>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </template>
        </template>
    </SettingsPageLayout>
</template>
